<template>
    <div id="app">
        <div class="popover-backdrop" v-if="dateBadgeOpen" v-teleport @click="dateBadgeOpen = false"></div>
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-dot"></div>
                    <div>
                        <h1 class="dashboard-title">Quick Dashboard</h1>
                        <div class="dashboard-subtitle">Fleet fuel, tank stock and station overview</div>
                    </div>
                </div>
                <div class="header-controls">
                    <div class="date-range-badge" @click="dateBadgeOpen = !dateBadgeOpen">
                        <span>{{ currentPeriodLabel }}</span>
                        <span class="date-range-chevron" :class="{ open: dateBadgeOpen }">&#9662;</span>

                        <div class="date-range-popover" v-if="dateBadgeOpen" @click.stop>
                            <div class="date-quick-buttons">
                                <button v-for="preset in datePresets" :key="preset.days"
                                    :class="['date-quick-btn', { active: activePreset === preset.days }]"
                                    @click="applyPreset(preset.days)">
                                    {{ preset.label }}
                                </button>
                            </div>
                            <div class="popover-controls">
                                <input type="date" v-model="params.fromDate" class="specific-date-input" @change="activePreset = null" />
                                <input type="date" v-model="params.toDate" class="specific-date-input" @change="activePreset = null" />
                            </div>
                            <div class="popover-controls" style="margin-top:10px;">
                                <v-select class="w-full" placeholder="All Stores" :options="storeDropDownData" v-model="storeDropDown"></v-select>
                            </div>
                            <div class="popover-controls">
                                <v-select class="w-full" placeholder="All Equipment" :options="equipmentData" v-model="equipmentDropDown"></v-select>
                            </div>
                            <button class="popover-apply-btn" :disabled="loading" @click="dateBadgeOpen = false; loadData();">Apply</button>
                        </div>
                    </div>
                    <button class="header-refresh-btn" :disabled="loading" @click="loadData">
                        <span class="live-dot"></span> Refresh
                    </button>
                </div>
            </div>
        </div>

        <div class="main-content">
            <div v-if="loading" class="loading-overlay-inline"><div class="loader"></div></div>

            <template v-else-if="dashboard">
                <!-- Hero: total volume dispensed -->
                <div class="hero-banner">
                    <div>
                        <div class="hero-label">Total Volume Dispensed <span class="hero-label-tag">Diesel Only</span></div>
                        <div class="hero-value">{{ formatLitres(dashboard.totalLitresDispensed) }}</div>
                        <div class="hero-caption">
                            across {{ dashboard.totalTransactions.toLocaleString() }} transactions &middot;
                            {{ dashboard.activeEquipmentCount.toLocaleString() }} units &middot;
                            {{ dashboard.costCentresBilledCount.toLocaleString() }} cost centres
                        </div>
                    </div>
                    <div class="hero-icon">
                        <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="14" y="14" width="22" height="38" rx="3" />
                            <rect x="18" y="19" width="14" height="8" rx="1" stroke-width="2" />
                            <line x1="18" y1="34" x2="32" y2="34" stroke-width="2" />
                            <line x1="18" y1="40" x2="32" y2="40" stroke-width="2" />
                            <path d="M36 24 h6 a4 4 0 0 1 4 4 v16" />
                            <path d="M42 40 l6 4 l-6 4" />
                            <line x1="10" y1="52" x2="40" y2="52" stroke-width="2" />
                        </svg>
                    </div>
                </div>

                <!-- Low stock warning -->
                <div class="low-stock-banner" v-if="dashboard.lowStockTankCount > 0" @click="openTankModal('Tank Stock Levels')">
                    <span class="low-stock-icon">&#9888;</span>
                    <span><strong>{{ dashboard.lowStockTankCount }}</strong> tank(s) below 20% capacity - click to view tank stock levels.</span>
                </div>

                <!-- Fleet overview KPIs -->
                <div class="section-card">
                <p class="section-heading">Fleet Overview</p>
                <div class="kpi-grid">
                    <div class="kpi-card kpi-card-clickable" @click="openTransactionsModal('Total Litres Dispensed - All Transactions')">
                        <div class="kpi-icon kpi-icon-orange">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2 C8 8 6 11 6 14 a6 6 0 0 0 12 0 C18 11 16 8 12 2 Z" /></svg>
                        </div>
                        <div class="kpi-value">{{ formatLitres(dashboard.totalLitresDispensed) }}</div>
                        <div class="kpi-label">Total Litres Dispensed <span class="kpi-label-tag">Diesel Only</span></div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" v-if="hasTransfers" @click="openTransferModal('Total Diesel Transferred - Transactions')">
                        <div class="kpi-icon kpi-icon-indigo">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h13l-3-3M20 17H7l3 3" /></svg>
                        </div>
                        <div class="kpi-value">{{ formatLitres(dashboard.transferred.currentTotal) }}</div>
                        <div class="kpi-label">Total Diesel Transferred</div>
                        <div :class="trendClass(dashboard.transferred.trendPercent)">{{ formatTrend(dashboard.transferred.trendPercent) }} vs previous period</div>
                    </div>
                    <div class="kpi-card" v-if="hasReceiving">
                        <div class="kpi-icon kpi-icon-blue">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v13m0 0l-4-4m4 4l4-4" /><path d="M4 19h16" /></svg>
                        </div>
                        <div class="kpi-value">{{ formatLitres(dashboard.received.currentTotal) }}</div>
                        <div class="kpi-label">Total Fuel Received</div>
                        <div :class="trendClass(dashboard.received.trendPercent)">{{ formatTrend(dashboard.received.trendPercent) }} vs previous period</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" @click="openEquipmentModal('Total Equipment - Volume by Equipment')">
                        <div class="kpi-icon kpi-icon-blue">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="9" width="13" height="8" rx="1" /><path d="M15 11h4l3 3v3h-7z" /><circle cx="6.5" cy="19" r="1.6" /><circle cx="17.5" cy="19" r="1.6" /></svg>
                        </div>
                        <div class="kpi-value">{{ dashboard.totalEquipmentCount.toLocaleString() }}</div>
                        <div class="kpi-label">Total Equipment</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" @click="openEquipmentModal('Active Equipment - Volume by Equipment')">
                        <div class="kpi-icon kpi-icon-green">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.5 2.5L16 9" /></svg>
                        </div>
                        <div class="kpi-value">{{ dashboard.activeEquipmentCount.toLocaleString() }}</div>
                        <div class="kpi-label">Active Equipment</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" @click="openTransactionsModal('Total Transactions - All Transactions')">
                        <div class="kpi-icon kpi-icon-purple">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 3h8l4 4v14H7z" /><path d="M15 3v4h4" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="15" y2="16" /></svg>
                        </div>
                        <div class="kpi-value">{{ dashboard.totalTransactions.toLocaleString() }}</div>
                        <div class="kpi-label">Total Transactions</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" @click="openTransactionsModal('Avg Litres / Transaction - All Transactions')">
                        <div class="kpi-icon kpi-icon-teal">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 17l5-5 4 4 8-9" /><path d="M15 7h5v5" /></svg>
                        </div>
                        <div class="kpi-value">{{ formatLitres(dashboard.avgLitresPerTransaction) }}</div>
                        <div class="kpi-label">Avg Litres / Transaction</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" v-if="dashboard.totalCostCentreCount > 0" @click="openCostCentreModal('Cost Centres Billed - Cost Centre Split')">
                        <div class="kpi-icon kpi-icon-indigo">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 21V9l8-6 8 6v12" /><path d="M9 21v-7h6v7" /></svg>
                        </div>
                        <div class="kpi-value">{{ dashboard.costCentresBilledCount.toLocaleString() }}</div>
                        <div class="kpi-label">Cost Centres Billed</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" v-if="dashboard.tankLevels.length" @click="openTankModal('Tank Stock Levels')">
                        <div class="kpi-icon kpi-icon-green">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2 C8 8 6 11 6 14 a6 6 0 0 0 12 0 C18 11 16 8 12 2 Z" /></svg>
                        </div>
                        <div class="kpi-value">{{ dashboard.currentStockPercentFull != null ? dashboard.currentStockPercentFull + '%' : '-' }}</div>
                        <div class="kpi-label">Current Stock Level</div>
                        <div class="stat-change">{{ formatLitres(dashboard.currentStockVolume) }} of {{ formatLitres(dashboard.currentStockCapacity) }}</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" @click="openStationModal('Active Stations')">
                        <div class="kpi-icon kpi-icon-purple">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" /></svg>
                        </div>
                        <div class="kpi-value">{{ dashboard.totalActiveStoreCount.toLocaleString() }}</div>
                        <div class="kpi-label">Active Stations</div>
                        <div class="stat-change">{{ dashboard.storesWithNoActivityCount }} with no activity this period</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" v-if="dashboard.totalEmployeeCount > 0" @click="openEmployeeModal('Total Employees - Employees')">
                        <div class="kpi-icon kpi-icon-teal">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 20v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                        </div>
                        <div class="kpi-value">{{ dashboard.totalEmployeeCount.toLocaleString() }}</div>
                        <div class="kpi-label">Total Employees</div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" v-if="dashboard.totalEmployeeCount > 0" @click="openEmployeeModal('Active Employees - Employees')">
                        <div class="kpi-icon kpi-icon-orange">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 20v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                        </div>
                        <div class="kpi-value">{{ dashboard.activeEmployeeCount.toLocaleString() }}</div>
                        <div class="kpi-label">Active Employees</div>
                        <div class="stat-change">dispensed diesel this period</div>
                    </div>
                </div>
                </div>

                <!-- Analytics: trend (full width, roomier) + top equipment/employees -->
                <div class="section-card">
                <p class="section-heading">Analytics</p>
                <div class="charts-section">
                    <div class="chart-card chart-card-wide">
                        <div class="chart-header chart-header-clickable" @click="openTransactionsModal('Volume Trend - All Transactions')">
                            <p class="chart-title">Volume Trend</p>
                            <div class="chart-header-stats" v-if="dashboard.trendSeries.length">
                                <div class="chart-header-stat">
                                    <span class="chart-header-stat-value">{{ formatLitres(dashboard.totalLitresDispensed) }}</span>
                                    <span class="chart-header-stat-label">Total, {{ dashboard.trendSeries.length }} days</span>
                                </div>
                                <div class="chart-header-stat">
                                    <span class="chart-header-stat-value">{{ formatLitres(dashboard.avgDispensedPerActiveDay) }}</span>
                                    <span class="chart-header-stat-label">Avg / Active Day</span>
                                </div>
                                <div class="chart-header-stat" v-if="dashboard.peakDay">
                                    <span class="chart-header-stat-value">{{ formatLitres(dashboard.peakDay.dispensedLitres) }}</span>
                                    <span class="chart-header-stat-label">Peak - {{ formatDateOnly(dashboard.peakDay.date) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="chart-body">
                            <vue-apex-charts type="area" height="360" :options="trendChartOptions" :series="trendChartSeries" v-if="dashboard.trendSeries.length"></vue-apex-charts>
                            <div class="empty-description" v-else>No dispensing recorded in this period.</div>
                        </div>
                    </div>
                </div>
                </div>

                <!-- Tank stock levels -->
                <div class="section-card" v-if="dashboard.tankLevels.length">
                <p class="section-heading">Tank Stock Levels</p>
                <div class="tank-grid">
                    <div class="tank-card" v-for="tank in dashboard.tankLevels" :key="tank.tankId" @click="openTankModal('Tank Stock Levels')">
                        <svg class="tank-visual-svg" viewBox="0 0 200 120" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <clipPath :id="'tankClip' + tank.tankId">
                                    <rect x="15" y="25" width="170" height="70" rx="35" ry="35" />
                                </clipPath>
                            </defs>
                            <!-- empty (unfilled) body -->
                            <rect x="15" y="25" width="170" height="70" rx="35" ry="35" fill="#fff" />
                            <!-- liquid fill, bottom-anchored -->
                            <rect
                                x="15" width="170"
                                :y="25 + 70 - (70 * tankPercent(tank) / 100)"
                                :height="70 * tankPercent(tank) / 100"
                                :fill="tankFillColor(tank)" :clip-path="'url(#tankClip' + tank.tankId + ')'" />
                            <!-- gauge ruler (left) -->
                            <line x1="60" y1="33" x2="60" y2="87" stroke="#1B3A5C" stroke-width="2" />
                            <line v-for="n in 8" :key="'g' + n" :x1="54" :x2="64" :y1="33 + (n - 1) * 7.7" :y2="33 + (n - 1) * 7.7" stroke="#1B3A5C" stroke-width="1.5" />
                            <!-- ladder (right) -->
                            <line x1="148" y1="33" x2="148" y2="87" stroke="#1B3A5C" stroke-width="2.5" />
                            <line x1="164" y1="33" x2="164" y2="87" stroke="#1B3A5C" stroke-width="2.5" />
                            <line v-for="n in 4" :key="'r' + n" x1="148" x2="164" :y1="40 + (n - 1) * 13" :y2="40 + (n - 1) * 13" stroke="#1B3A5C" stroke-width="2.5" />
                            <!-- bottom foot notches -->
                            <path d="M62 88 v10 h16 v-10" fill="#fff" stroke="#1B3A5C" stroke-width="2.5" />
                            <path d="M122 88 v10 h16 v-10" fill="#fff" stroke="#1B3A5C" stroke-width="2.5" />
                            <!-- outline -->
                            <rect x="15" y="25" width="170" height="70" rx="35" ry="35" fill="none" stroke="#1B3A5C" stroke-width="4" />
                            <!-- top dome -->
                            <rect x="75" y="8" width="50" height="18" rx="9" ry="9" fill="#1B3A5C" />
                        </svg>
                        <div class="tank-card-name">{{ tank.tankName }}</div>
                        <div class="tank-card-store">{{ tank.storeName }}</div>
                        <div class="tank-card-percent" :class="tankStatusClass(tank)">{{ tank.percentFull != null ? tank.percentFull + '%' : '-' }}</div>
                        <div class="tank-card-rows">
                            <div class="tank-card-row"><span>Current</span><span>{{ formatLitres(tank.currentVolume) }}</span></div>
                            <div class="tank-card-row"><span>Capacity</span><span>{{ formatLitres(tank.capacity) }}</span></div>
                            <div class="tank-card-row"><span>Updated</span><span>{{ formatDate(tank.lastReadingDate) }}</span></div>
                        </div>
                    </div>
                </div>
                </div>

                <!-- Volume by equipment / employees -->
                <div class="section-card">
                <p class="section-heading">Volume by Equipment</p>
                <div class="analytics-grid-even" :class="{ 'single-col': !topEmployees.length }">
                    <div class="chart-card">
                        <div class="chart-header">
                            <p class="chart-title">Equipment <span class="chart-title-note">(Top 5)</span></p>
                            <button class="chart-header-link" @click="openEquipmentModal('Volume by Equipment - Full List')">View all</button>
                        </div>
                        <div class="bar-list">
                            <div class="bar-list-row bar-list-row-clickable" v-for="eq in topEquipment" :key="eq.equipmentId" @click="openTransactionsModal(eq.equipmentName + ' - Transactions', { type: 'equipment', value: eq.equipmentId })">
                                <div class="bar-list-label">{{ eq.equipmentName }} <span class="bar-list-sub">({{ eq.registration || 'No reg.' }})</span></div>
                                <div class="bar-list-track"><div class="bar-list-fill" :style="{ width: barWidth(eq.dispensedLitres, topEquipment) }"></div></div>
                                <div class="bar-list-value">{{ formatLitres(eq.dispensedLitres) }}</div>
                            </div>
                            <div class="empty-description" v-if="!topEquipment.length">No dispensing recorded in this period.</div>
                        </div>
                    </div>
                    <div class="chart-card" v-if="topEmployees.length">
                        <div class="chart-header">
                            <p class="chart-title">Employees <span class="chart-title-note">(Top 5)</span></p>
                            <button class="chart-header-link" @click="openEmployeeModal('Employees - Full List')">View all</button>
                        </div>
                        <div class="bar-list">
                            <div class="bar-list-row bar-list-row-clickable" v-for="op in topEmployees" :key="op.operatorName" @click="openTransactionsModal(op.operatorName + ' - Transactions', { type: 'operator', value: op.operatorName })">
                                <div class="bar-list-label">{{ op.operatorName }} <span class="bar-list-sub">({{ op.transactionCount }} txns)</span></div>
                                <div class="bar-list-track"><div class="bar-list-fill" :style="{ width: barWidth(op.dispensedLitres, topEmployees) }"></div></div>
                                <div class="bar-list-value">{{ formatLitres(op.dispensedLitres) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>

                <!-- Cost centre split - hidden entirely when this account has no
                     cost centres billed in the period, rather than showing an
                     empty panel. -->
                <div class="section-card" v-if="dashboard.totalCostCentreCount > 0">
                    <p class="section-heading">Cost Centre Split</p>
                    <div class="charts-section">
                        <div class="chart-card chart-card-wide">
                            <div class="bar-list bar-list-padded">
                                <div class="bar-list-row bar-list-row-clickable" v-for="cc in dashboard.costCentreBreakdown" :key="cc.costCentreName" @click="openTransactionsModal(cc.costCentreName + ' - Transactions', { type: 'costCentre', value: cc.costCentreName })">
                                    <div class="bar-list-label">{{ cc.costCentreName }}</div>
                                    <div class="bar-list-track"><div class="bar-list-fill bar-list-fill-blue" :style="{ width: barWidth(cc.dispensedLitres, dashboard.costCentreBreakdown) }"></div></div>
                                    <div class="bar-list-value">{{ formatLitres(cc.dispensedLitres) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Volume by station - same rule: hidden when there's nothing
                     to show rather than an empty "no activity" panel. -->
                <div class="section-card" v-if="dashboard.storeBreakdown.length">
                    <p class="section-heading">Volume by Station</p>
                    <div class="charts-section">
                        <div class="chart-card chart-card-wide">
                            <div class="bar-list bar-list-padded">
                                <div class="bar-list-row bar-list-row-clickable" v-for="s in dashboard.storeBreakdown" :key="s.storeId" @click="openTransactionsModal(s.storeName + ' - Transactions', { type: 'store', value: s.storeName })">
                                    <div class="bar-list-label">{{ s.storeName }}</div>
                                    <div class="bar-list-track"><div class="bar-list-fill bar-list-fill-blue" :style="{ width: barWidth(s.dispensedLitres + s.transferredLitres + s.receivedLitres, storeVolumeTotals) }"></div></div>
                                    <div class="bar-list-value">{{ formatLitres(s.dispensedLitres + s.transferredLitres + s.receivedLitres) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Top consumers -->
                <div class="section-card">
                <p class="section-heading">Top Consumers</p>
                <div class="charts-section">
                    <div class="chart-card chart-card-wide">
                        <div class="driver-table-wrapper">
                            <table class="data-table" v-if="topConsumers.length">
                                <thead>
                                    <tr>
                                        <th>Registration</th>
                                        <th>Equipment</th>
                                        <th>Class</th>
                                        <th class="num">Volume (L)</th>
                                        <th class="num">Txns</th>
                                        <th class="num">Avg L/HR</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr class="data-table-row-clickable" v-for="eq in topConsumers" :key="eq.equipmentId" @click="openTransactionsModal(eq.equipmentName + ' - Transactions', { type: 'equipment', value: eq.equipmentId })">
                                        <td>{{ eq.registration || '-' }}</td>
                                        <td>{{ eq.equipmentName }}</td>
                                        <td><span class="type-badge type-badge-class">{{ eq.className }}</span></td>
                                        <td class="num">{{ formatLitres(eq.dispensedLitres) }}</td>
                                        <td class="num">{{ eq.transactionCount }}</td>
                                        <td class="num">{{ eq.litresPerHour != null ? eq.litresPerHour : '-' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="empty-description" v-else>No dispensing recorded in this period.</div>
                        </div>
                    </div>
                </div>
                </div>
            </template>

            <div v-else-if="loadFailed" class="empty-state">
                <div class="empty-icon">&#9888;</div>
                <div class="empty-title">Couldn't load the dashboard</div>
                <div class="empty-description">Something went wrong fetching this data - try adjusting the date range/filters or refreshing.</div>
            </div>
            <div v-else class="empty-state">
                <div class="empty-icon">&#9878;</div>
                <div class="empty-title">No diesel data yet</div>
                <div class="empty-description">Diesel dispensing captured via Manual Entries will show up here.</div>
            </div>
        </div>

        <!-- Drill-down modal: opened by clicking a KPI card, a bar-list row or a
             table row, so every panel's underlying data can be inspected rather
             than only ever showing the summarized view. -->
        <vs-popup class="drill-down-popup" :title="modalTitle" :active.sync="modalActive">
            <div class="driver-table-wrapper drill-down-table-wrapper">
                <table class="data-table" v-if="modalKind === 'transactions' && modalTransactions.length">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Station</th>
                            <th>Equipment</th>
                            <th>Registration</th>
                            <th>Cost Centre</th>
                            <th>Employee</th>
                            <th class="num">Volume (L)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(t, idx) in modalTransactions" :key="idx">
                            <td>{{ formatDate(t.date) }}</td>
                            <td>{{ t.storeName }}</td>
                            <td>{{ t.equipmentName }}</td>
                            <td>{{ t.registration || '-' }}</td>
                            <td>{{ t.costCentreName }}</td>
                            <td>{{ t.operatorName }}</td>
                            <td class="num">{{ formatLitres(t.volume) }}</td>
                        </tr>
                    </tbody>
                </table>
                <table class="data-table" v-else-if="modalKind === 'transfer' && dashboard && dashboard.transferredTransactions.length">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Station</th>
                            <th>Equipment</th>
                            <th>Registration</th>
                            <th>Cost Centre</th>
                            <th>Employee</th>
                            <th class="num">Volume (L)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(t, idx) in dashboard.transferredTransactions" :key="idx">
                            <td>{{ formatDate(t.date) }}</td>
                            <td>{{ t.storeName }}</td>
                            <td>{{ t.equipmentName }}</td>
                            <td>{{ t.registration || '-' }}</td>
                            <td>{{ t.costCentreName }}</td>
                            <td>{{ t.operatorName }}</td>
                            <td class="num">{{ formatLitres(t.volume) }}</td>
                        </tr>
                    </tbody>
                </table>
                <table class="data-table" v-else-if="modalKind === 'equipment' && dashboard && dashboard.equipmentBreakdown.length">
                    <thead>
                        <tr>
                            <th>Registration</th>
                            <th>Equipment</th>
                            <th>Class</th>
                            <th class="num">Volume (L)</th>
                            <th class="num">Txns</th>
                            <th class="num">Avg L/HR</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="data-table-row-clickable" v-for="eq in dashboard.equipmentBreakdown" :key="eq.equipmentId" @click="openTransactionsModal(eq.equipmentName + ' - Transactions', { type: 'equipment', value: eq.equipmentId })">
                            <td>{{ eq.registration || '-' }}</td>
                            <td>{{ eq.equipmentName }}</td>
                            <td><span class="type-badge type-badge-class">{{ eq.className }}</span></td>
                            <td class="num">{{ formatLitres(eq.dispensedLitres) }}</td>
                            <td class="num">{{ eq.transactionCount }}</td>
                            <td class="num">{{ eq.litresPerHour != null ? eq.litresPerHour : '-' }}</td>
                        </tr>
                    </tbody>
                </table>
                <table class="data-table" v-else-if="modalKind === 'costcentre' && dashboard && dashboard.costCentreBreakdown.length">
                    <thead>
                        <tr>
                            <th>Cost Centre</th>
                            <th class="num">Volume (L)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="data-table-row-clickable" v-for="cc in dashboard.costCentreBreakdown" :key="cc.costCentreName" @click="openTransactionsModal(cc.costCentreName + ' - Transactions', { type: 'costCentre', value: cc.costCentreName })">
                            <td>{{ cc.costCentreName }}</td>
                            <td class="num">{{ formatLitres(cc.dispensedLitres) }}</td>
                        </tr>
                    </tbody>
                </table>
                <table class="data-table" v-else-if="modalKind === 'employee' && dashboard && dashboard.operatorBreakdown.length">
                    <thead>
                        <tr>
                            <th>Employee</th>
                            <th class="num">Volume (L)</th>
                            <th class="num">Txns</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="data-table-row-clickable" v-for="op in dashboard.operatorBreakdown" :key="op.operatorName" @click="openTransactionsModal(op.operatorName + ' - Transactions', { type: 'operator', value: op.operatorName })">
                            <td>{{ op.operatorName }}</td>
                            <td class="num">{{ formatLitres(op.dispensedLitres) }}</td>
                            <td class="num">{{ op.transactionCount }}</td>
                        </tr>
                    </tbody>
                </table>
                <table class="data-table" v-else-if="modalKind === 'station' && dashboard && dashboard.stationBreakdown.length">
                    <thead>
                        <tr>
                            <th>Station</th>
                            <th class="num">Dispensed (L)</th>
                            <th class="num">Transferred (L)</th>
                            <th class="num">Received (L)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="data-table-row-clickable" v-for="s in dashboard.stationBreakdown" :key="s.storeId" @click="openTransactionsModal(s.storeName + ' - Transactions', { type: 'store', value: s.storeName })">
                            <td>{{ s.storeName }}</td>
                            <td class="num">{{ formatLitres(s.dispensedLitres) }}</td>
                            <td class="num">{{ formatLitres(s.transferredLitres) }}</td>
                            <td class="num">{{ formatLitres(s.receivedLitres) }}</td>
                        </tr>
                    </tbody>
                </table>
                <table class="data-table" v-else-if="modalKind === 'tank' && dashboard && dashboard.tankLevels.length">
                    <thead>
                        <tr>
                            <th>Tank</th>
                            <th>Station</th>
                            <th class="num">Current (L)</th>
                            <th class="num">Capacity (L)</th>
                            <th class="num">% Full</th>
                            <th>Last Reading</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="tank in dashboard.tankLevels" :key="tank.tankId">
                            <td>{{ tank.tankName }}</td>
                            <td>{{ tank.storeName }}</td>
                            <td class="num">{{ formatLitres(tank.currentVolume) }}</td>
                            <td class="num">{{ formatLitres(tank.capacity) }}</td>
                            <td class="num">{{ tank.percentFull != null ? tank.percentFull + '%' : '-' }}</td>
                            <td>{{ formatDate(tank.lastReadingDate) }}</td>
                        </tr>
                    </tbody>
                </table>
                <div class="empty-description" v-else>No data to display.</div>
            </div>
        </vs-popup>
    </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import vSelect from 'vue-select';

export default {
    name: 'DieselDashboard',
    components: { VueApexCharts, 'v-select': vSelect },
    directives: {
        teleport: {
            inserted: function (el) { document.body.appendChild(el); }
        }
    },
    data: function () {
        var today = new Date();
        return {
            loading: false,
            dashboard: null,
            loadFailed: false,
            dateBadgeOpen: false,
            // Defaults to a rolling 6 months rather than Year-to-Date - pulling
            // a full calendar year of raw transactions (needed for the
            // equipment/cost-centre/store/employee breakdowns and the
            // transaction drill-down) was timing out on accounts with a lot of
            // dispensing history. Year to Date is still available as an
            // explicit preset for anyone who wants the wider view.
            activePreset: 180,
            datePresets: [
                { days: 7, label: '7 Days' },
                { days: 30, label: '30 Days' },
                { days: 90, label: '90 Days' },
                { days: 180, label: '6 Months' },
                { days: 'ytd', label: 'Year to Date' }
            ],
            params: {
                fromDate: (function () {
                    var from = new Date();
                    from.setDate(from.getDate() - 179);
                    return from.toISOString().slice(0, 10);
                })(),
                toDate: today.toISOString().slice(0, 10)
            },
            storeDropDown: null,
            storeDropDownData: [],
            // Drill-down modal state - opened by clicking a KPI card, a bar-list
            // row or a table row. modalKind picks which table the popup shows
            // ('transactions', 'equipment' or 'costcentre'); modalFilter narrows
            // the transaction list to one equipment/cost centre when set.
            modalActive: false,
            modalTitle: '',
            modalKind: 'transactions',
            modalFilter: null,
            equipmentDropDown: null,
            equipmentData: []
        };
    },
    computed: {
        currentPeriodLabel: function () {
            if (this.activePreset === 'ytd') return 'Year to Date';
            return this.params.fromDate + ' → ' + this.params.toDate;
        },
        // Transfers and receiving only get their own KPI card/trend series once
        // there is at least one such transaction in the selected period - most
        // sites only dispense and never transfer or manually receive, so an
        // empty card/series for those would just be noise.
        hasTransfers: function () {
            return !!this.dashboard && this.dashboard.transferred.currentTotal > 0;
        },
        hasReceiving: function () {
            return !!this.dashboard && this.dashboard.received.currentTotal > 0;
        },
        trendChartOptions: function () {
            var dates = (this.dashboard ? this.dashboard.trendSeries : []).map(function (p) { return p.date.slice(0, 10); });
            var colors = ['#D2691E'];
            if (this.hasTransfers) colors.push('#7986CB');
            if (this.hasReceiving) colors.push('#2196F3');
            return {
                chart: { toolbar: { show: false }, zoom: { enabled: false }, background: 'transparent' },
                colors: colors,
                fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: .35, opacityTo: 0, stops: [0, 90, 100] } },
                stroke: { curve: 'smooth', width: 2 },
                markers: { size: 3, strokeWidth: 0, hover: { size: 5 } },
                grid: { borderColor: '#e2e8f0' },
                xaxis: { categories: dates, labels: { style: { colors: '#64748b' }, rotate: -45, rotateAlways: false } },
                yaxis: { labels: { style: { colors: '#64748b' }, formatter: function (v) { return Number(v).toLocaleString(); } } },
                legend: { show: this.hasTransfers || this.hasReceiving, position: 'top' },
                dataLabels: { enabled: false },
                tooltip: { theme: 'light', x: { format: 'dd MMM yyyy' } }
            };
        },
        trendChartSeries: function () {
            var series = this.dashboard ? this.dashboard.trendSeries : [];
            var result = [{ name: 'Dispensed', data: series.map(function (p) { return Math.round(p.dispensedLitres * 100) / 100; }) }];
            if (this.hasTransfers) result.push({ name: 'Transferred', data: series.map(function (p) { return Math.round(p.transferredLitres * 100) / 100; }) });
            if (this.hasReceiving) result.push({ name: 'Received', data: series.map(function (p) { return Math.round(p.receivedLitres * 100) / 100; }) });
            return result;
        },
        // Wrapper list so the generic barWidth(value, list) helper (which reads
        // item.dispensedLitres to find the max) can scale the "Volume by Store"
        // bars against each store's combined dispensed+transferred+received total.
        storeVolumeTotals: function () {
            return (this.dashboard ? this.dashboard.storeBreakdown : [])
                .map(function (s) { return { dispensedLitres: s.dispensedLitres + s.transferredLitres + s.receivedLitres }; });
        },
        topEquipment: function () {
            return (this.dashboard ? this.dashboard.equipmentBreakdown : []).slice(0, 5);
        },
        topConsumers: function () {
            return (this.dashboard ? this.dashboard.equipmentBreakdown : []).slice(0, 10);
        },
        topEmployees: function () {
            return (this.dashboard ? this.dashboard.operatorBreakdown : []).slice(0, 5);
        },
        // Full transaction list behind the drill-down modal, narrowed to one
        // equipment/cost centre when modalFilter is set (e.g. clicking a bar
        // row or a Top Consumers row), otherwise every transaction in the
        // selected period.
        modalTransactions: function () {
            var all = this.dashboard ? this.dashboard.transactions : [];
            var filter = this.modalFilter;
            if (!filter) return all;
            if (filter.type === 'equipment') return all.filter(function (t) { return t.equipmentId === filter.value; });
            if (filter.type === 'costCentre') return all.filter(function (t) { return t.costCentreName === filter.value; });
            if (filter.type === 'store') return all.filter(function (t) { return t.storeName === filter.value; });
            if (filter.type === 'operator') return all.filter(function (t) { return t.operatorName === filter.value; });
            return all;
        }
    },
    methods: {
        openTransactionsModal: function (title, filter) {
            this.modalTitle = title;
            this.modalKind = 'transactions';
            this.modalFilter = filter || null;
            this.modalActive = true;
        },
        openEquipmentModal: function (title) {
            this.modalTitle = title;
            this.modalKind = 'equipment';
            this.modalFilter = null;
            this.modalActive = true;
        },
        openCostCentreModal: function (title) {
            this.modalTitle = title;
            this.modalKind = 'costcentre';
            this.modalFilter = null;
            this.modalActive = true;
        },
        openTankModal: function (title) {
            this.modalTitle = title;
            this.modalKind = 'tank';
            this.modalFilter = null;
            this.modalActive = true;
        },
        openTransferModal: function (title) {
            this.modalTitle = title;
            this.modalKind = 'transfer';
            this.modalFilter = null;
            this.modalActive = true;
        },
        openEmployeeModal: function (title) {
            this.modalTitle = title;
            this.modalKind = 'employee';
            this.modalFilter = null;
            this.modalActive = true;
        },
        openStationModal: function (title) {
            this.modalTitle = title;
            this.modalKind = 'station';
            this.modalFilter = null;
            this.modalActive = true;
        },
        // Matches the red/orange/blue thresholds already used for the
        // low-stock banner - under 20% is critical, under 50% is a warning.
        // Clamped 0-100 so a bad/stale reading never renders a fill wider than
        // the tank capsule itself.
        tankPercent: function (tank) {
            if (tank.percentFull == null) return 0;
            return Math.max(0, Math.min(100, tank.percentFull));
        },
        // Matches the red/orange/blue thresholds already used elsewhere on this
        // dashboard - under 20% is critical, under 50% is a warning.
        tankFillColor: function (tank) {
            var pct = this.tankPercent(tank);
            if (pct < 20) return '#E74C3C';
            if (pct < 50) return '#FF8C00';
            return '#A9C2D3';
        },
        tankStatusClass: function (tank) {
            var pct = this.tankPercent(tank);
            if (pct < 20) return 'tank-status-critical';
            if (pct < 50) return 'tank-status-warning';
            return 'tank-status-normal';
        },
        applyPreset: function (days) {
            var today = new Date();
            this.activePreset = days;
            this.params.toDate = today.toISOString().slice(0, 10);
            if (days === 'ytd') {
                this.params.fromDate = today.getFullYear() + '-01-01';
                return;
            }
            var from = new Date();
            from.setDate(today.getDate() - (days - 1));
            this.params.fromDate = from.toISOString().slice(0, 10);
        },
        buildQueryString: function () {
            var qs = 'startDate=' + encodeURIComponent(this.params.fromDate) + '&endDate=' + encodeURIComponent(this.params.toDate);
            if (this.storeDropDown) qs += '&storeId=' + this.storeDropDown.value;
            if (this.equipmentDropDown) qs += '&equipmentId=' + this.equipmentDropDown.value;
            return qs;
        },
        loadData: function () {
            var self = this;
            self.loading = true;
            self.loadFailed = false;
            var succeeded = false;
            var onSuccess = function (response) { self.dashboard = response.data; succeeded = true; };
            // $ajaxGet's own .catch() already shows an error toast but always
            // resolves (never rejects), so the only way to tell "the request
            // actually failed" apart from "it's genuinely just empty" is to
            // check whether onSuccess ran by the time onFinally fires.
            var onFinally = function () {
                self.loading = false;
                if (!succeeded) {
                    self.loadFailed = true;
                }
            };
            self.$ajaxGet(self, 'DieselDashboard?' + self.buildQueryString(), onSuccess, onFinally);
        },
        getStores: function () {
            var self = this;
            var onSuccess = function (response) {
                self.storeDropDownData = response.data.map(function (item) { return { label: item.name, value: item.id }; });
            };
            self.$ajaxGet(self, 'Stores/GetStoreForManualEntry', onSuccess);
        },
        getEquipment: function () {
            var self = this;
            var onSuccess = function (response) {
                self.equipmentData = response.data.map(function (item) { return { label: item.registration, value: item.id }; });
            };
            self.$ajaxGet(self, 'Equipments/GetEquipmentDropDown', onSuccess);
        },
        // Bar length relative to the largest value in the same list, so the
        // longest bar in each panel always reaches 100% instead of being
        // scaled against some arbitrary fixed maximum.
        // Linear width (value/max) breaks down when one row is orders of
        // magnitude bigger than the rest (e.g. 788,000 L vs a long tail of
        // 200-6,000 L rows) - every small row rounds to the same sliver and
        // a hard minimum-width floor just makes them all identical instead.
        // A log scale compresses the one huge outlier while still spreading
        // the smaller values out proportionally to each other, so a 5,860 L
        // row and a 242 L row end up visibly different lengths.
        barWidth: function (value, list) {
            var max = list.reduce(function (m, item) { return Math.max(m, item.dispensedLitres); }, 0);
            if (!max || !value) return '0%';
            var pct = (Math.log(value + 1) / Math.log(max + 1)) * 100;
            return Math.max(pct, 2) + '%';
        },
        formatLitres: function (value) {
            if (value == null) return '0 L';
            return Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' L';
        },
        trendClass: function (pct) {
            if (pct == null) return 'stat-change';
            return pct >= 0 ? 'stat-change positive' : 'stat-change';
        },
        formatTrend: function (pct) {
            if (pct == null) return '—';
            return (pct >= 0 ? '↑ ' : '↓ ') + Math.abs(pct) + '%';
        },
        formatDate: function (value) {
            if (!value) return '-';
            return new Date(value).toLocaleString();
        },
        // Date-only (no time) - for daily-aggregated values like the trend
        // chart's peak day, where the timestamp is always midnight and just
        // adds noise.
        formatDateOnly: function (value) {
            if (!value) return '-';
            return new Date(value).toLocaleDateString();
        }
    },
    created: function () {
        this.getStores();
        this.getEquipment();
        this.loadData();
    }
};
</script>

<style scoped>
#app { background: #e2e8f0; min-height: 100%; }

.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
    flex-shrink: 0;
    position: relative;
    z-index: 45;
}
.popover-backdrop {
    position: fixed; inset: 0; z-index: 40;
    background: rgba(15, 23, 42, .6); backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px);
}
.header-content { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 0 20px; }
.logo-section { display: flex; align-items: center; gap: 12px; }
.logo-dot { width: 12px; height: 12px; border-radius: 50%; background: #fff; box-shadow: 0 0 0 4px rgba(255, 255, 255, .3); }
.dashboard-title { color: white; font-size: 1.2rem; font-weight: 700; margin: 0; letter-spacing: .02em; }
.dashboard-subtitle { color: rgba(255, 255, 255, .85); font-size: .78rem; }
.header-controls { display: flex; align-items: center; gap: 12px; }
.header-refresh-btn {
    padding: 8px 16px; background: rgba(255, 255, 255, .2); color: #fff; border: 1px solid rgba(255, 255, 255, .5);
    border-radius: 20px; font-size: .8rem; font-weight: 600; cursor: pointer; transition: all .2s;
    display: inline-flex; align-items: center; gap: 8px;
}
.header-refresh-btn:hover { background: rgba(255, 255, 255, .35); }
.header-refresh-btn:disabled { opacity: .6; cursor: not-allowed; }
.live-dot { width: 8px; height: 8px; background: #4CAF50; border-radius: 50%; animation: pulse 2s infinite; display: inline-block; }
@keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, .7); }
    70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
    100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

.date-range-badge {
    position: relative; display: flex; align-items: center; gap: 8px; cursor: pointer;
    background: rgba(255, 255, 255, .2); color: #fff; padding: 8px 16px; border-radius: 20px;
    font-size: .8rem; font-weight: 600;
}
.date-range-chevron { transition: transform .2s; font-size: .7rem; }
.date-range-chevron.open { transform: rotate(180deg); }
.date-range-popover {
    position: absolute; top: calc(100% + 10px); right: 0; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, .2); padding: 16px; min-width: 280px; z-index: 50; cursor: default;
}
.popover-controls { display: flex; gap: 10px; margin-top: 10px; }
.popover-apply-btn {
    margin-top: 12px; width: 100%; padding: 8px 16px; background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: #fff; border: none; border-radius: 6px; font-size: .875rem; font-weight: 600; cursor: pointer;
}
.popover-apply-btn:disabled { opacity: .6; cursor: not-allowed; }

.main-content { flex: 1; overflow-y: auto; padding: 20px; background: #e2e8f0; }
.main-content > * { max-width: 1600px; margin-left: auto; margin-right: auto; }

.loading-overlay-inline { display: flex; justify-content: center; padding: 60px 0; }
.loader { width: 40px; height: 40px; border: 4px solid #f1f5f9; border-top-color: #D2691E; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.date-quick-buttons { display: flex; gap: 4px; flex-wrap: wrap; }
.date-quick-btn {
    padding: 5px 10px; border: 1px solid #cbd5e1; background: white; border-radius: 5px;
    font-size: .75rem; font-weight: 500; color: #64748b; cursor: pointer; transition: all .2s ease;
}
.date-quick-btn:hover { border-color: #D2691E; color: #D2691E; background: #fff9f5; }
.date-quick-btn.active { background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); color: white; border-color: #D2691E; }

.specific-date-input {
    padding: 5px 10px; border: 1px solid #cbd5e1; border-radius: 5px; font-size: .85rem;
    color: #334155; background: white; width: 100%;
}
.specific-date-input:focus { outline: none; border-color: #D2691E; box-shadow: 0 0 0 2px rgba(210, 105, 30, .15); }

.section-heading {
    font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
    color: #D2691E; margin: 28px 0 12px;
}
.section-heading:first-of-type { margin-top: 4px; }

/* Each dashboard section sits in its own bordered/shadowed card, with the
   heading pulled inside as a divider-topped title instead of floating text
   on the plain page background. */
.section-card {
    background: #fff; border: 1px solid #e2e8f0; border-radius: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08); padding: 20px 22px; margin-bottom: 24px;
}
.section-card .section-heading {
    margin: 0 0 16px; padding-bottom: 12px; border-bottom: 1px solid #f1f5f9;
}
.section-card .chart-card, .section-card .tank-card {
    box-shadow: none; border: 1px solid #f1f5f9; background: #fafbfc;
}
.section-card .kpi-grid, .section-card .charts-section,
.section-card .analytics-grid-even, .section-card .tank-grid { margin-bottom: 0; }

.hero-banner {
    background: linear-gradient(135deg, #FF8C00 0%, #D2691E 100%);
    border-radius: 16px; padding: 26px 30px; display: flex; justify-content: space-between; align-items: center;
    box-shadow: 0 8px 24px rgba(210, 105, 30, .2); margin-bottom: 8px;
}
.hero-label { color: rgba(255, 255, 255, .85); font-size: .78rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; }
.hero-label-tag { background: rgba(255, 255, 255, .2); border-radius: 8px; padding: 2px 10px; font-weight: 700; font-size: 1rem; letter-spacing: 0; text-transform: none; margin-left: 4px; }
.hero-value { color: #fff; font-size: 2.4rem; font-weight: 800; line-height: 1.15; }
.hero-caption { color: rgba(255, 255, 255, .85); font-size: .85rem; font-weight: 500; margin-top: 2px; }
.hero-icon { width: 64px; height: 64px; color: rgba(255, 255, 255, .3); flex-shrink: 0; }
.hero-icon svg { width: 100%; height: 100%; }

.low-stock-banner {
    display: flex; align-items: center; gap: 10px; background: #FEF3E2; border: 1px solid #FBBF24;
    color: #92400E; border-radius: 10px; padding: 12px 18px; margin-bottom: 16px; font-size: .85rem; font-weight: 500;
    cursor: pointer; transition: background .2s;
}
.low-stock-banner:hover { background: #FDE9C8; }
.low-stock-icon { font-size: 1.1rem; flex-shrink: 0; }

.analytics-grid-even { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
.analytics-grid-even.single-col { grid-template-columns: 1fr; }

.kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 16px; margin-bottom: 0; }
.kpi-card {
    background: #fff; border-radius: 14px; padding: 18px; box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    border: 1px solid #e2e8f0; border-left: 4px solid #cbd5e1; transition: all .25s;
    display: flex; flex-direction: column;
}
.kpi-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0, 0, 0, .1); }
.kpi-card-clickable { cursor: pointer; }
.kpi-card-clickable:hover { border-color: #D2691E; }
/* decorative accent bar + left border, colored to match each card's icon */
.kpi-card::after {
    content: ''; display: block; height: 6px; border-radius: 3px; background: #f1f5f9;
    margin-top: auto; padding-top: 12px; background-clip: content-box;
}
.kpi-card:has(.kpi-icon-orange) { border-left-color: #FF8C00; }
.kpi-card:has(.kpi-icon-orange)::after { background: linear-gradient(90deg, #FF8C00, #FFD9A8); }
.kpi-card:has(.kpi-icon-blue) { border-left-color: #2196F3; }
.kpi-card:has(.kpi-icon-blue)::after { background: linear-gradient(90deg, #2196F3, #BBDEFB); }
.kpi-card:has(.kpi-icon-green) { border-left-color: #27AE60; }
.kpi-card:has(.kpi-icon-green)::after { background: linear-gradient(90deg, #27AE60, #A9DFBF); }
.kpi-card:has(.kpi-icon-red) { border-left-color: #E74C3C; }
.kpi-card:has(.kpi-icon-red)::after { background: linear-gradient(90deg, #E74C3C, #F5B7B1); }
.kpi-card:has(.kpi-icon-purple) { border-left-color: #9C27B0; }
.kpi-card:has(.kpi-icon-purple)::after { background: linear-gradient(90deg, #9C27B0, #E1BEE7); }
.kpi-card:has(.kpi-icon-teal) { border-left-color: #26A69A; }
.kpi-card:has(.kpi-icon-teal)::after { background: linear-gradient(90deg, #26A69A, #B2DFDB); }
.kpi-card:has(.kpi-icon-indigo) { border-left-color: #7986CB; }
.kpi-card:has(.kpi-icon-indigo)::after { background: linear-gradient(90deg, #7986CB, #C5CAE9); }
.kpi-icon {
    width: 38px; height: 38px; border-radius: 10px; margin-bottom: 12px;
    display: flex; align-items: center; justify-content: center;
}
.kpi-icon svg { width: 20px; height: 20px; }
.kpi-icon-orange { background: #FFF1E0; color: #FF8C00; }
.kpi-icon-blue { background: #E3F2FD; color: #2196F3; }
.kpi-icon-green { background: #E8F8EF; color: #27AE60; }
.kpi-icon-red { background: #FDECEA; color: #E74C3C; }
.kpi-icon-purple { background: #F3E8FA; color: #9C27B0; }
.kpi-icon-teal { background: #E6F7F3; color: #26A69A; }
.kpi-icon-indigo { background: #E8EAF6; color: #7986CB; }
.kpi-value { font-size: 1.55rem; color: #1e293b; font-weight: 800; line-height: 1.2; }
.kpi-label { color: #64748b; font-size: .78rem; font-weight: 600; margin-top: 2px; }
.kpi-label-tag { background: #eef2f7; color: #64748b; border-radius: 8px; padding: 1px 8px; font-weight: 700; font-size: .85rem; margin-left: 2px; }
.stat-change { font-size: .75rem; margin-top: 4px; color: #94a3b8; }
.stat-change.positive { color: #27AE60; }

.charts-section { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 20px; margin-bottom: 20px; }
.chart-card { background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); border: 1px solid #e2e8f0; overflow: hidden; }
.chart-card-wide { grid-column: 1 / -1; }
.chart-header { padding: 14px 18px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.chart-header-clickable { cursor: pointer; }
.chart-header-clickable:hover { background: #fff8f3; }
.chart-title { margin: 0; font-size: .95rem; font-weight: 700; color: #34495E; }
.chart-title-note { font-size: .75rem; font-weight: 500; color: #94a3b8; }
.chart-header-stats { display: flex; align-items: center; gap: 22px; }
.chart-header-stat { text-align: right; }
.chart-header-stat-value { display: block; font-size: 1.1rem; font-weight: 800; color: #D2691E; }
.chart-header-stat-label { display: block; font-size: .68rem; color: #94a3b8; text-transform: uppercase; letter-spacing: .04em; white-space: nowrap; }
.chart-header-link {
    background: none; border: 1px solid #D2691E; color: #D2691E; border-radius: 14px; padding: 3px 12px;
    font-size: .72rem; font-weight: 700; cursor: pointer; transition: all .2s;
}
.chart-header-link:hover { background: #D2691E; color: #fff; }
.chart-body { padding: 16px; }

/* ApexCharts renders its own DOM outside Vue's scoping, so this needs the
   deep combinator to reach the tooltip and recolor it to match the
   dashboard's orange brand instead of the library's plain gray default. */
.chart-body >>> .apexcharts-tooltip {
    border: 1px solid #FBD9B4 !important; border-radius: 10px !important;
    box-shadow: 0 8px 20px rgba(210, 105, 30, .18) !important; overflow: hidden;
}
.chart-body >>> .apexcharts-tooltip-title {
    background: linear-gradient(135deg, #FF8C00 0%, #D2691E 100%) !important;
    color: #fff !important; font-weight: 700 !important; border-bottom: none !important;
}

.bar-list { padding: 8px 20px 16px; }
.bar-list-padded { padding: 16px 20px; }
.bar-list-row { display: grid; grid-template-columns: 1fr 2fr auto; align-items: center; gap: 14px; padding: 9px 0; }
.bar-list-row-clickable { cursor: pointer; border-radius: 8px; margin: 0 -8px; padding: 9px 8px; }
.bar-list-row-clickable:hover { background: #fff8f3; }
.bar-list-label { font-size: .85rem; color: #334155; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bar-list-sub { color: #94a3b8; font-weight: 500; font-size: .75rem; }
.bar-list-track { height: 8px; background: #f1f5f9; border-radius: 5px; overflow: hidden; }
.bar-list-fill { height: 100%; background: linear-gradient(90deg, #FF8C00, #D2691E); border-radius: 5px; }
.bar-list-fill-blue { background: linear-gradient(90deg, #2196F3, #64B5F6); }
.bar-list-value { font-size: .82rem; font-weight: 700; color: #1e293b; text-align: right; white-space: nowrap; }

.driver-table-wrapper { padding: 4px 20px 16px; overflow-x: auto; }
.drill-down-table-wrapper { padding: 16px 20px 20px; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table thead { background: #f8fafc; }
.data-table th { padding: 10px 14px; text-align: left; color: #64748b; font-weight: 700; font-size: .72rem; text-transform: uppercase; letter-spacing: .05em; border-bottom: 2px solid #e2e8f0; }
.data-table th.num { text-align: right; }
.data-table tbody tr { border-bottom: 1px solid #e2e8f0; background: #fff; }
.data-table tbody tr:nth-child(even) { background: #fafbfc; }
.data-table tbody tr:hover { background: #fff8f3; }
.data-table td { padding: 10px 14px; font-size: .875rem; color: #334155; }
.data-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
.data-table-row-clickable { cursor: pointer; }
.data-table tbody td:first-child { font-weight: 700; color: #D2691E; }
.data-table th:first-child { color: #D2691E; }

.drill-down-popup >>> .vs-popup {
    max-width: 900px; width: 92vw; border-radius: 14px; overflow: hidden;
    box-shadow: 0 20px 50px rgba(0, 0, 0, .25);
}
.drill-down-popup >>> .vs-popup--header {
    background: linear-gradient(135deg, #FF8C00 0%, #D2691E 100%);
    border-radius: 0; padding: 14px 48px 14px 18px; min-height: 20px;
}
.drill-down-popup >>> .vs-popup--title h3 {
    color: #fff; font-weight: 700; font-size: 1.05rem;
}
/* Vuesax normally floats this button half outside the header via a
   translate transform, but the popup's overflow:hidden (for its own rounded
   corners) was clipping that overhang - pin it fully inside the header
   instead of relying on the overhang. */
.drill-down-popup >>> .vs-popup--close {
    position: absolute !important; top: 50% !important; right: 16px !important;
    transform: translateY(-50%) !important; margin: 0 !important;
    background: rgba(255, 255, 255, .2) !important; color: #fff !important; box-shadow: none !important;
    width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;
    font-size: 1.1rem !important;
}
.drill-down-popup >>> .vs-popup--close:hover { background: rgba(255, 255, 255, .38) !important; }
.drill-down-popup >>> .vs-popup--content { max-height: 65vh; overflow-y: auto; padding: 0; margin: 0; width: 100%; }

.type-badge {
    display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: .72rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: .03em;
}
.type-badge-class { background: #E3F2FD; color: #1565C0; }

.tank-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; margin-bottom: 20px; }
.tank-card {
    background: #fff; border-radius: 14px; padding: 20px; box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    border: 1px solid #e2e8f0; text-align: center; transition: all .25s; cursor: pointer;
}
.tank-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0, 0, 0, .1); }
.tank-visual-svg { width: 160px; height: auto; margin: 6px auto 10px; display: block; }
.tank-card-name { font-size: .95rem; font-weight: 700; color: #34495E; }
.tank-card-store { font-size: .75rem; color: #94a3b8; margin-bottom: 6px; }
.tank-card-percent { font-size: 1.7rem; font-weight: 800; margin-bottom: 10px; }
.tank-status-normal { color: #27AE60; }
.tank-status-warning { color: #FF8C00; }
.tank-status-critical { color: #E74C3C; }
.tank-card-rows { text-align: left; border-top: 1px solid #f1f5f9; padding-top: 10px; }
.tank-card-row { display: flex; justify-content: space-between; gap: 8px; font-size: .78rem; color: #64748b; padding: 3px 0; }
.tank-card-row span:last-child { color: #334155; font-weight: 600; text-align: right; }

.empty-state { text-align: center; padding: 60px 20px; background: #fff; border-radius: 12px; border: 1px solid #cbd5e1; }
.empty-icon { font-size: 2.5rem; margin-bottom: 12px; }
.empty-title { font-size: 1.1rem; font-weight: 700; color: #34495E; margin-bottom: 6px; }
.empty-description { color: #64748b; font-size: .9rem; padding: 12px 0; text-align: center; }

@media (max-width: 640px) {
    .header-content { flex-direction: column; align-items: flex-start; gap: 10px; }
    .main-content { padding: 12px; }
    .kpi-grid, .charts-section, .analytics-grid-even { grid-template-columns: 1fr; }
    .hero-banner { flex-direction: column; align-items: flex-start; gap: 16px; }
    .date-range-popover { right: auto; left: 0; min-width: 240px; }
}
</style>
